import { amazonConnectApi, type SellerConnection } from '../../api/amazonconnect';
import { warehouseMasterApi, type Warehouse } from '../../api/warehouseMaster';
import { compareWindow, periodWindow } from '../executive-kpis/periodWindows';
import type { CompareMode, DatePreset } from '../types';
import { buildIndiaMapFromReportGeo } from './aggregateStates';
import type { IndiaMapModel } from './types';
import { buildWarehouseMapLayer } from './warehouseLayer';

/** Paginate Warehouse Master list (single API; no new REST). Soft-fail if master offline. */
async function loadAllWarehouses(): Promise<{
  items: Warehouse[];
  error: string | null;
}> {
  try {
    const pageSize = 200;
    const first = await warehouseMasterApi.list('', '', pageSize, 0);
    const items = [...(first.items ?? [])];
    const total = first.total ?? items.length;
    let offset = items.length;
    // ponytail: hard cap pages so a huge master never hangs the map load
    let pages = 1;
    while (offset < total && pages < 10) {
      const page = await warehouseMasterApi.list('', '', pageSize, offset);
      const batch = page.items ?? [];
      if (batch.length === 0) break;
      items.push(...batch);
      offset += batch.length;
      pages += 1;
    }
    return { items, error: null };
  } catch (e) {
    return {
      items: [],
      error: e instanceof Error ? e.message : 'Warehouse Master unavailable',
    };
  }
}

function marketplaceLabel(connection: SellerConnection): string {
  return (
    connection.marketplace_ids?.[0] ??
    connection.marketplaces?.find((m) => m.enabled)?.marketplace_id ??
    'Amazon'
  );
}

export async function loadIndiaMap(opts: {
  connection: SellerConnection | null;
  datePreset: DatePreset;
  compare: CompareMode;
}): Promise<IndiaMapModel | null> {
  const { connection, datePreset, compare } = opts;
  if (!connection?.id) return null;

  const period = periodWindow(datePreset);
  const cmp = compareWindow(period, compare);
  const tz = 'Asia/Kolkata';
  const marketplace = marketplaceLabel(connection);

  const [geo, priorGeo, wh] = await Promise.all([
    amazonConnectApi
      .getAllOrdersGeo(connection.id, { from: period.isoFrom, to: period.isoTo, tz })
      .catch(() => null),
    cmp
      ? amazonConnectApi
          .getAllOrdersGeo(connection.id, { from: cmp.isoFrom, to: cmp.isoTo, tz })
          .catch(() => null)
      : Promise.resolve(null),
    loadAllWarehouses(),
  ]);

  const model = buildIndiaMapFromReportGeo({
      connectionId: connection.id,
      sellerLabel: connection.seller_ref ?? connection.id,
      marketplace: String(marketplace),
      period,
      compareLabel: cmp ? cmp.label : compare === 'none' ? null : compare,
      geo: {
        cities: geo?.cities ?? [],
        states: geo?.states ?? [],
        currency: geo?.currency,
        line_count: geo?.line_count ?? 0,
        source: geo?.source,
      },
      prior: priorGeo
        ? {
            cities: priorGeo.cities ?? [],
            states: priorGeo.states ?? [],
            currency: priorGeo.currency,
            line_count: priorGeo.line_count ?? 0,
            source: priorGeo.source,
          }
        : null,
    });

  const layer = buildWarehouseMapLayer(wh.items);
  return {
    ...model,
    warehouses: layer.warehouses,
    warehousePins: layer.pins,
    warehouseCoverageStates: layer.coverageStateCodes,
    warehouseMasterTotal: layer.warehouses.length,
    warehouseMissingCoords: layer.missingCoords,
    warehouseLoadError: wh.error,
  };
}
